import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, FileText, Presentation, ArrowLeft, ArrowRight, Maximize, Download, X } from "lucide-react";
import { api, apiErr, fileUrl } from "../api";
import { FullLoader, Empty } from "../ui";

export default function SlidesTab() {
  const [items, setItems] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/presentations"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); setItems((x) => x || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <FullLoader />;

  const upload = async (file) => {
    if (!file) return;
    const t = title.trim() || file.name.replace(/\.[^.]+$/, "");
    setBusy(true);
    const fd = new FormData(); fd.append("title", t); fd.append("file", file);
    try { await api.post("/presentations", fd); toast.success("Presentación subida"); setTitle(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const del = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.title}"?`)) return;
    try { await api.delete(`/presentations/${it.id}`); toast.success("Eliminada"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (opcional)" className="pn-field !w-auto min-w-[240px]" />
        <label className={`pn-btn pn-btn-primary ${busy ? "opacity-60" : "cursor-pointer"}`}>
          {busy ? <Loader2 size={17} className="pn-spin" /> : <Plus size={17} />} Subir presentación (PDF o PPTX)
          <input ref={ref} type="file" accept="application/pdf,.pdf,.pptx,.ppt" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files[0])} />
        </label>
      </div>
      {items.length === 0 ? <Empty icon={Presentation}>Sube tu primera presentación. Los PDF se ven directo en el visor; los PPTX se descargan.</Empty> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={it.id} className="pn-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${it.kind === "pdf" ? "pn-soft-brand" : "pn-soft-accent"}`}>{it.kind === "pdf" ? <FileText size={20} /> : <Presentation size={20} />}</div>
                <button onClick={() => del(it)} className="pn-icon-btn" title="Eliminar"><Trash2 size={15} /></button>
              </div>
              <p className="font-bold pn-ink truncate">{it.title}</p>
              <p className="text-xs pn-muted uppercase tracking-wide mb-4">{it.kind} · {new Date(it.created_at).toLocaleDateString("es-EC")}</p>
              <button onClick={() => setIdx(i)} className="pn-btn pn-btn-primary w-full">Abrir visor</button>
            </div>
          ))}
        </div>
      )}
      {idx !== null && <SlideViewer items={items} idx={idx} setIdx={setIdx} onClose={() => setIdx(null)} />}
    </div>
  );
}

function SlideViewer({ items, idx, setIdx, onClose }) {
  const box = useRef(null);
  const it = items[idx];
  const src = fileUrl(it.file?.path);
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((idx + 1) % items.length);
      if (e.key === "ArrowLeft") setIdx((idx - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [idx, items.length, onClose, setIdx]);
  return (
    <div className="fixed inset-0 z-[70] flex flex-col p-3 sm:p-6" style={{ background: "rgba(3,7,18,.96)" }}>
      <div className="flex items-center justify-between mb-3 text-white">
        <p className="font-bold truncate pr-4">{it.title} <span className="text-white/40 text-sm font-normal">· {idx + 1}/{items.length}</span></p>
        <button onClick={onClose} className="text-white/70 hover:text-white" aria-label="Cerrar visor"><X size={24} /></button>
      </div>
      <div ref={box} className="flex-1 rounded-2xl overflow-hidden bg-white flex items-center justify-center">
        {it.kind === "pdf" ? <iframe title={it.title} src={src} className="w-full h-full" /> : (
          <div className="text-center p-10 text-slate-800">
            <Presentation size={52} className="mx-auto mb-4 text-sky-500" />
            <p className="font-bold text-xl mb-2">{it.title}</p>
            <p className="text-slate-500 mb-6">Los archivos PPTX no se pueden mostrar en el navegador. Descárgalo, o súbelo como PDF para verlo aquí.</p>
            <a href={src} download={it.file?.filename} className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3 font-semibold"><Download size={18} /> Descargar</a>
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-6 text-white/80 font-semibold text-sm">
        <button onClick={() => setIdx((idx - 1 + items.length) % items.length)} className="inline-flex items-center gap-2 hover:text-white"><ArrowLeft size={17} /> Anterior</button>
        <button onClick={() => box.current?.requestFullscreen?.()} className="inline-flex items-center gap-2 hover:text-white"><Maximize size={17} /> Pantalla completa</button>
        <a href={src} download={it.file?.filename} className="inline-flex items-center gap-2 hover:text-white"><Download size={17} /> Descargar</a>
        <button onClick={() => setIdx((idx + 1) % items.length)} className="inline-flex items-center gap-2 hover:text-white">Siguiente <ArrowRight size={17} /></button>
      </div>
    </div>
  );
}
